import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { MapPin } from 'lucide-react';
import { Label } from './ui/label';

/**
 * Shows a location picker dropdown ONLY when the customer has 2+ locations.
 * Returns null (renders nothing) otherwise — no extra friction for simple customers.
 *
 * Props:
 *   locations  – array of { id, label, address1, city, state, zip }
 *   value      – currently selected customer_location_id (or '' / null)
 *   onChange   – (locationId: string | null) => void
 *   disabled   – boolean
 *   className  – optional wrapper class
 */
export function LocationPicker({ locations, value, onChange, disabled, className }) {
  if (!locations || locations.length < 2) return null;

  const formatAddr = (loc) => {
    const parts = [loc.address1, loc.city, loc.state].filter(Boolean);
    return parts.length ? parts.join(', ') : 'No address';
  };

  return (
    <div className={className} data-testid="location-picker">
      <Label className="text-xs flex items-center gap-1 mb-1">
        <MapPin className="w-3 h-3" /> Service Location
      </Label>
      <Select
        value={value || '__default__'}
        onValueChange={(v) => onChange(v === '__default__' ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger data-testid="location-picker-trigger">
          <SelectValue placeholder="Default service address" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__default__">Default service address</SelectItem>
          {locations.map((loc) => (
            <SelectItem key={loc.id} value={loc.id} data-testid={`location-option-${loc.id}`}>
              <span className="font-medium">{loc.label || 'Unnamed'}</span>
              <span className="text-muted-foreground ml-1.5">— {formatAddr(loc)}</span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Read-only badge that shows the selected location's label + address.
 * Returns null if no location is set.
 */
export function LocationBadge({ location, className }) {
  if (!location) return null;

  const addr = [location.address1, location.city, location.state, location.zip].filter(Boolean).join(', ');

  return (
    <div className={`flex items-start gap-1.5 text-sm text-muted-foreground ${className || ''}`} data-testid="location-badge">
      <MapPin className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />
      <span>
        <span className="font-medium text-foreground">{location.label}</span>
        {addr && <span className="ml-1">— {addr}</span>}
      </span>
    </div>
  );
}
